import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, ImageIcon, FileText, Search, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { attachEvidence, fetchFileAsBlobUrl, listFiles } from '../api/client';

const KIND_TILE_ICON = {
  photo: ImageIcon, site_plan: ImageIcon, site_note: FileText,
  research: FileText, technical_doc: FileText, client_doc: FileText,
  unknown: FileText,
};

const KIND_LABEL = {
  photo: 'Photo', site_plan: 'Plan', site_note: 'Note',
  research: 'Research', technical_doc: 'Tech doc', client_doc: 'Client doc',
  unknown: 'Unclassified',
};

const fmtCapture = (iso, tz) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  const s = d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  return tz ? `${s} ${tz}` : s;
};

const CAPTURE_UNAVAILABLE = 'Original capture time unavailable';

const PickerTile = ({ file, selected, onToggle }) => {
  const [thumbUrl, setThumbUrl] = useState(null);
  useEffect(() => {
    let blob = null;
    let cancelled = false;
    if (file.thumbnail_path) {
      fetchFileAsBlobUrl(file.id, 'thumbnail').then((url) => {
        if (cancelled) { URL.revokeObjectURL(url); return; }
        blob = url;
        setThumbUrl(url);
      }).catch(() => {});
    }
    return () => { cancelled = true; if (blob) URL.revokeObjectURL(blob); };
  }, [file.id, file.thumbnail_path]);
  const Icon = KIND_TILE_ICON[file.kind] || FileText;
  const capture = fmtCapture(file.capture_datetime, file.capture_timezone);
  // Only photos/plans/notes claim a capture-time slot; other kinds don't
  // have one to display.
  const wantsCapture = ['photo', 'site_plan', 'site_note'].includes(file.kind);
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={`picker-tile-${file.reference}`}
      className={`
        relative rounded-lg border overflow-hidden text-left transition
        ${selected ? 'border-emerald-400 ring-2 ring-emerald-400/40' : 'border-slate-700 hover:border-slate-500'}
      `}
    >
      <div className="aspect-square bg-slate-950 flex items-center justify-center">
        {thumbUrl
          ? <img src={thumbUrl} alt={file.original_filename} className="w-full h-full object-cover" />
          : <Icon className="w-10 h-10 text-slate-500 opacity-60" />}
      </div>
      <div className="p-2">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-mono font-semibold text-emerald-400">{file.reference}</span>
          <span className="text-[10px] text-slate-500">{KIND_LABEL[file.kind] || file.kind}</span>
        </div>
        <div className="text-[11px] text-slate-300 truncate mt-0.5" title={file.original_filename}>
          {file.original_filename}
        </div>
        {wantsCapture && (
          <div
            className={`text-[10px] font-mono mt-0.5 ${capture ? 'text-slate-500' : 'text-red-400/70 italic'}`}
            data-testid={`picker-capture-${file.reference}`}
            title={capture ? `EXIF DateTimeOriginal${file.capture_timezone ? ' (' + file.capture_timezone + ')' : ''}` : 'No EXIF DateTimeOriginal in this image'}
          >
            {capture || CAPTURE_UNAVAILABLE}
          </div>
        )}
      </div>
      {selected && (
        <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-emerald-500 text-slate-950 flex items-center justify-center">
          <Check className="w-3 h-3" strokeWidth={3} />
        </div>
      )}
    </button>
  );
};

/**
 * Fast photo picker for manual evidence attachment.
 *
 * Design goals per the assessor's brief:
 *   - never leave the outcome-review workflow
 *   - grid of thumbnails with P### reference and capture date/time
 *   - filename + P### search
 *   - kind filter (photo / plan / note / research / tech / client)
 *   - multi-select
 *   - one optional note applied to every selected link
 *   - immediate status update on save (no AI reanalysis required)
 */
export const EvidencePickerDialog = ({ open, onOpenChange, projectId, outcome, onAttached }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    listFiles(projectId).then((all) => {
      if (cancelled) return;
      // Assessment result files and archives cannot be evidence.
      const usable = all.filter((f) => f.kind !== 'assessment_result' && f.kind !== 'archive');
      setFiles(usable);
    }).catch(() => toast.error('Failed to load project files'))
      .finally(() => !cancelled && setLoading(false));
    setSelected(new Set());
    setNote('');
    setSearch('');
    setKind('all');
    return () => { cancelled = true; };
  }, [open, projectId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return files
      .filter((f) => kind === 'all' || f.kind === kind)
      .filter((f) => !q ||
        f.reference.toLowerCase().includes(q) ||
        (f.original_filename || '').toLowerCase().includes(q) ||
        (f.notes || '').toLowerCase().includes(q))
      .sort((a, b) => {
        // Capture datetime primary, upload order fallback.
        const ta = a.capture_datetime || '';
        const tb = b.capture_datetime || '';
        if (ta && tb) return ta.localeCompare(tb);
        return (a.upload_order || 0) - (b.upload_order || 0);
      });
  }, [files, search, kind]);

  const toggle = useCallback((id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }, []);

  const submit = async () => {
    if (selected.size === 0) return;
    setSaving(true);
    try {
      const results = await Promise.all(
        Array.from(selected).map((fid) => attachEvidence(outcome.id, fid, note || null))
      );
      toast.success(`Linked ${results.length} evidence item${results.length === 1 ? '' : 's'}`);
      onAttached?.(results);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to attach evidence');
    } finally {
      setSaving(false);
    }
  };

  const kindOptions = [
    ['all', 'All'],
    ['photo', 'Photos'],
    ['site_plan', 'Plans'],
    ['site_note', 'Notes'],
    ['research', 'Research'],
    ['technical_doc', 'Tech docs'],
    ['client_doc', 'Client docs'],
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="evidence-picker">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Attach evidence
            {outcome && (
              <span className="text-xs text-slate-400 font-normal">
                → {outcome.label}
                {outcome.value !== null && outcome.value !== undefined && (
                  <span className="ml-1 font-mono text-slate-500">= {String(outcome.value)}{outcome.unit ? ' ' + outcome.unit : ''}</span>
                )}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 py-2 border-y border-slate-800">
          <div className="relative flex-1">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              data-testid="picker-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search P###, filename or notes…"
              className="pl-8 bg-slate-950 border-slate-700 h-8 text-sm"
              autoFocus
            />
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {kindOptions.map(([k, label]) => (
              <button
                key={k} type="button"
                onClick={() => setKind(k)}
                data-testid={`picker-filter-${k}`}
                className={`text-[11px] px-2 py-1 rounded-full border transition ${
                  kind === k
                    ? 'bg-slate-100 text-slate-950 border-slate-100'
                    : 'bg-slate-900 text-slate-300 border-slate-700 hover:border-slate-500'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto py-3">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-sm text-slate-500">
              {files.length === 0
                ? 'No evidence files in this project yet. Upload photos, notes or research and try again.'
                : 'No files match your search.'}
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {filtered.map((f) => (
                <PickerTile
                  key={f.id}
                  file={f}
                  selected={selected.has(f.id)}
                  onToggle={() => toggle(f.id)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-slate-800 pt-3 space-y-2">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-slate-500">Assessor note (optional)</span>
            <Textarea
              data-testid="picker-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder='e.g. "Spacer bar date visible in photograph."'
              rows={2}
              className="mt-1 bg-slate-950 border-slate-700 text-sm resize-none"
            />
          </label>
          <DialogFooter className="gap-2 sm:justify-between">
            <span className="text-xs text-slate-400 self-center" data-testid="picker-selection-count">
              {selected.size} selected
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                onClick={submit}
                disabled={saving || selected.size === 0}
                data-testid="picker-submit"
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-medium"
              >
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" />}
                Attach {selected.size > 0 ? `(${selected.size})` : ''}
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
